import axios, { AxiosResponse } from "axios";
import { WebAPI, IFailCallbackParam } from "../constants";
import { getErrorStatus } from "./getErrorStatus";
import AuthService from "../../common/AuthService";
import { toLocaleString } from "./common";

function AxiosGet(url: string, token?: string) {
  if (token) {
    return axios.get(url, {
      headers: { Authorization: "Bearer " + token },
    });
  } else {
    return axios.get(url);
  }
}

interface IGetFilelistsResponceModel {
  /**
   * プロジェクトID
   */
  projectid: string;
  /**
   * ファイルID
   */
  fileid: string;
  /**
   * ファイル名（オリジナル）
   */
  name: string;
  /**
   * 入力データ数
   */
  inchnum: number;
  /**
   * 出力データ数
   */
  outchnum: number;
  /**
   * データ数
   */
  datanum: number;
  /**
   * サンプリング周期[msec]
   */
  interval: number;
  /**
   * データの開始時刻
   */
  starttime: string;
  /**
   * データの終了時刻
   */
  endtime: string;
  /**
   * ファイル種別
   */
  filetype: string;
  /**
   * 最終更新時刻
   */
  update: string;
}

export interface IFileRow extends IGetFilelistsResponceModel {
  /**
   * 一連番号(DataGridで必須)
   */
  id: number;
}

interface ICallbackParams extends IFailCallbackParam {
  /**
   * WebAPIからファイル一覧の取得が成功した場合に呼び出されます
   * @callback success
   * @param {IFileRow[]} rows - ファイル一覧
   * @param {IProtRow[]} prots - チャートデータ一覧
   * @param {number} status - httpステータス
   */
  success: (rows: IFileRow[], status: number) => void;
}

interface IAxiosInternalParams extends ICallbackParams {
  promise: Promise<AxiosResponse>;
}

function AxiosInternal(params: IAxiosInternalParams) {
  params.promise
    .then((res) => {
      const datas: IGetFilelistsResponceModel[] = res.data;
      const rows: IFileRow[] = []; 
      datas.forEach((data, i) => {
        rows.push({
          projectid: data.projectid,
          id: i,
          fileid: data.fileid,
          name: data.name,
          inchnum: data.inchnum,
          outchnum: data.outchnum,
          datanum: data.datanum,
          interval: data.interval,
          starttime: toLocaleString(new Date(Date.parse(data.starttime))),
          endtime: toLocaleString(new Date(Date.parse(data.endtime))),
          filetype: data.filetype,
          update: toLocaleString(new Date(Date.parse(data.update))),
        }); 
      });
      params.success(rows, res.status);
    })
    .catch((err) => {
      const { id, status } = getErrorStatus(err);
      params.fail(id, status);
    });
}

export interface IGetFilesParams extends ICallbackParams {
  /**
   * 認証オブジェクト
   */
  authService?: AuthService;
  /**
   * プロジェクトID
   */
  projectid: string;
}

/**
 * データファイル一覧のみを取得します
 * @function getFileLists
 * @param  {IGetFilesParams} params - ファイル一覧取得パラメータ 
 */
export function getFileLists(params: IGetFilesParams): void {
  const { authService, projectid } = params;
  const url = WebAPI.GET_DATAFILES(projectid);
  if (authService) {
    authService.getToken({
      success: (token) => {
        AxiosInternal({
          promise: AxiosGet(url, token),
          success: params.success,
          fail: (id, status) => {
            if (status === 401) {
              getFilelistsRetry401(authService, url, params);
            } else {
              params.fail(id, status);
            }
          },
        });
      },
      fail: (err) => {
        const { id, status } = getErrorStatus(err);
        params.fail(id, status);
      },
    });
  } else {
    AxiosInternal({
      promise: AxiosGet(url),
      success: params.success,
      fail: params.fail,
    });
  }
}

/**
 * データファイル一覧のみを取得します
 * @param {AuthService} authService - 認証オブジェクト
 * @param {string} url - リクエストURL
 * @param {ICallbackParams} param - コールバックパラメータ
 */
export function getFilelistsRetry401(
  authService: AuthService,
  url: string,
  param: ICallbackParams
): void {
  authService.updateToken({
    success: (token) => {
      AxiosInternal({
        promise: AxiosGet(url, token),
        success: param.success,
        fail: param.fail,
      });
    },
    fail: (err) => {
      const { id, status } = getErrorStatus(err);
      param.fail(id, status);
    },
  });
}
